import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import {
  FiChevronDown as ChevronDown,
  FiHome as Building2,
  FiAlertCircle as AlertCircle,
  FiSettings as Settings,
} from 'react-icons/fi';

const ClientSelector: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { selectedClientId, accessibleClients, isLoadingClients, error, selectClient, hasClientAccess } = useClient();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!hasClientAccess) {
    return null;
  }

  const selectedClient = accessibleClients.find((c) => c.id === selectedClientId);

  const handleSelect = (clientId: string) => {
    selectClient(clientId);
    setIsOpen(false);
  };

  if (error) {
    return (
      <div className="relative mr-4">
        <div className="flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
          <AlertCircle className="w-4 h-4 mr-2" />
          <span>Error loading clients</span>
        </div>
      </div>
    );
  }

  if (isLoadingClients) {
    return (
      <div className="relative mr-4">
        <div className="flex items-center px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
          <span>Loading clients...</span>
        </div>
      </div>
    );
  }

  if (accessibleClients.length === 0) {
    return (
      <div className="relative mr-4">
        <div className="flex items-center px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
          <AlertCircle className="w-4 h-4 mr-2" />
          <span>No clients available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mr-4" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
      >
        <Building2 className="w-4 h-4 mr-2" />
        <span className="max-w-xs truncate">
          {selectedClient
            ? `${selectedClient.name}` + (selectedClient.partner_name ? ` (${selectedClient.partner_name})` : '')
            : 'Select Client...'}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 max-h-96 overflow-auto">
          <div className="py-1">
            {accessibleClients.map((client) => (
              <button
                key={client.id}
                onClick={() => handleSelect(client.id)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  selectedClientId === client.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-medium">{client.name}</div>
                {client.partner_name && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{client.partner_name}</div>
                )}
              </button>
            ))}
            {/* Manage Clients link for permitted users */}
            {(hasPermission(PERMISSIONS.CLIENTS_MANAGE) || hasPermission(PERMISSIONS.PARTNERS_MANAGE)) && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                <Link
                  to="/hub/clients"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center"
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Clients
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
